/**
 * WP-36 / MR-14 — WHAT AN "Open" BUTTON IS ACTUALLY FOR.
 *
 * Three surfaces render a column of buttons whose only accessible name is the
 * word "Open": the Records table (desktop rows and mobile cards) and the
 * activity feed. To anyone reading the screen that is fine — the row IS the
 * context. To anyone reading the accessibility tree it is a list of forty
 * identical controls, and the standard way to move through a page is to pull up
 * exactly that list.
 *
 * THE VISIBLE TEXT STAYS "Open". The column is narrow, the word is right, and
 * padding a button with the row it sits in would be noise for the reader who can
 * already see it. What changes is the ACCESSIBLE NAME, which is the one that
 * travels without the row.
 *
 * NOTHING NEW IS DISCLOSED. Every part of the name is already rendered in the
 * same row, in the same cells, to the same caller — this only puts it where a
 * control's name is read from.
 */

/** The record nouns, in the words the reader already sees on that surface. */
const SOURCE_NOUN: Record<string, string> = {
  visitor_log: 'visitor entry',
  // "BUILDING ACTIVITY", NOT "LOG ENTRY". The table is `daily_log` and every
  // user-facing surface in this product says Building Activities — filter-model
  // .tsx's BOOK_LABELS makes the same substitution for the same reason. Note
  // that record-detail.tsx's own SOURCE_LABEL still says 'Log entry' for this
  // source; that is a VISIBLE heading and changing it is not this package's
  // business, but the two now disagree and it is worth knowing.
  daily_log: 'building activity',
  package: 'parcel',
};

/** The same nouns keyed by the Records tab's book, which is what the reader
 *  chose there. Kept separate from SOURCE_NOUN on purpose: `recordRef` maps BOTH
 *  'Visitor Log' and 'Resident Log' onto the `visitor_log` source, so deriving
 *  the noun from the source would call a resident record a visitor entry. */
const BOOK_NOUN: Record<string, string> = {
  'Visitor Log': 'visitor record',
  'Resident Log': 'resident record',
  'Parcel Log': 'parcel',
  'Daily Log': 'building activity',
};

export const sourceNoun = (source: string | null | undefined): string =>
  (source && SOURCE_NOUN[source]) || 'record';

export const bookNoun = (book: string | null | undefined): string =>
  (book && BOOK_NOUN[book]) || 'record';

/**
 * "Open visitor record: Maria Silva, unit 4B, 11 Aug 14:32".
 *
 * EMPTY PARTS ARE DROPPED RATHER THAN PRINTED AS BLANKS. A record with no
 * readable name is an ordinary state here — names are encrypted at rest and a
 * row can outlive the account that wrote it — and "Open visitor record: , unit
 * 4B" is worse than saying less. With nothing at all to add it falls back to
 * "Open visitor record", which is still more than the bare word was.
 */
export const openRecordLabel = (
  noun: string,
  parts: (string | null | undefined)[]
): string => {
  const detail = parts
    .map(part => (part == null ? '' : String(part).trim()))
    .filter(Boolean)
    .join(', ');

  return detail ? `Open ${noun}: ${detail}` : `Open ${noun}`;
};
